import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Bell, CheckCircle, AlertTriangle, Info, XCircle } from 'lucide-react';
import axios from 'axios';

const WS_RECONNECT_DELAY = 3000; // 3 seconds
const WS_MAX_RECONNECT_ATTEMPTS = 3;
const POLLING_INTERVAL = 15000; // 15 seconds fallback polling

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useNotificationWebSocket(token, onNotification) {
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimeout = useRef(null);
  const pollingInterval = useRef(null);
  const lastNotificationId = useRef(null);
  const usePolling = useRef(false);

  // Fallback polling function
  const pollNotifications = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_URL}/notifications?unread_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const notifications = response.data;
      const newCount = notifications.length;
      
      // Check for new notifications
      if (notifications.length > 0) {
        const latestId = notifications[0].id;
        if (lastNotificationId.current && lastNotificationId.current !== latestId) {
          // New notification arrived
          const newNotif = notifications[0];
          
          const iconMap = {
            success: <CheckCircle className="w-5 h-5 text-green-500" />,
            warning: <AlertTriangle className="w-5 h-5 text-amber-500" />,
            error: <XCircle className="w-5 h-5 text-red-500" />,
            info: <Info className="w-5 h-5 text-blue-500" />,
          };
          
          toast(newNotif.title, {
            description: newNotif.message,
            icon: iconMap[newNotif.type] || <Bell className="w-5 h-5 text-primary" />,
            duration: 5000,
          });
          
          if (onNotification) {
            onNotification(newNotif);
          }
        }
        lastNotificationId.current = latestId;
      }
      
      setUnreadCount(newCount);
    } catch (error) {
      // console.error(/* PROD */ 'Failed to poll notifications:', error);
    }
  }, [token, onNotification]);

  // Start polling as fallback
  const startPolling = useCallback(() => {
    if (pollingInterval.current) return;
    
    // console.log('📡 Starting notification polling (fallback mode)');
    usePolling.current = true;
    setIsConnected(true); // Show as "connected" even in polling mode
    
    // Initial poll
    pollNotifications();
    
    // Set up interval
    pollingInterval.current = setInterval(pollNotifications, POLLING_INTERVAL);
  }, [pollNotifications]);

  const stopPolling = useCallback(() => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
    usePolling.current = false;
  }, []);

  const connect = useCallback(() => {
    if (!token) return;

    // Get WebSocket URL from backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) {
      // console.error(/* PROD */ 'REACT_APP_BACKEND_URL not configured');
      startPolling();
      return;
    }
    
    // Parse the backend URL properly
    const url = new URL(backendUrl);
    const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${url.host}/ws/notifications?token=${token}`;
    
    // console.log('🔌 Connecting to WebSocket...');

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // Set a timeout for connection
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          // console.log('⏱️ WebSocket connection timeout, falling back to polling');
          ws.close();
          startPolling();
        }
      }, 5000);

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        // console.log('🔌 WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
        stopPolling();
      };

      ws.onmessage = (event) => {
        try {
          // Handle ping/pong
          if (event.data === 'ping') {
            ws.send('pong');
            return;
          }
          if (event.data === 'pong') {
            return;
          }

          const message = JSON.parse(event.data);
          
          if (message.type === 'connected') {
            setUnreadCount(message.data.unread_count || 0);
          }
          
          if (message.type === 'new_notification') {
            const notif = message.data;
            setUnreadCount(prev => prev + 1);
            
            // Show toast notification
            const iconMap = {
              success: <CheckCircle className="w-5 h-5 text-green-500" />,
              warning: <AlertTriangle className="w-5 h-5 text-amber-500" />,
              error: <XCircle className="w-5 h-5 text-red-500" />,
              info: <Info className="w-5 h-5 text-blue-500" />,
            };
            
            toast(notif.title, {
              description: notif.message,
              icon: iconMap[notif.notif_type] || <Bell className="w-5 h-5 text-primary" />,
              duration: 5000,
            });
            
            // Call callback if provided
            if (onNotification) {
              onNotification(notif);
            }
          }
        } catch (e) {
          // console.error(/* PROD */ 'Failed to parse WebSocket message:', e);
        }
      };

      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        // console.log('🔌 WebSocket disconnected:', event.code);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt to reconnect or fall back to polling
        if (reconnectAttempts.current < WS_MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts.current += 1;
          // console.log(`Reconnecting... Attempt ${reconnectAttempts.current}/${WS_MAX_RECONNECT_ATTEMPTS}`);
          reconnectTimeout.current = setTimeout(connect, WS_RECONNECT_DELAY);
        } else {
          // console.log('⚠️ Max reconnect attempts reached, falling back to polling');
          startPolling();
        }
      };

      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        // console.error(/* PROD */ 'WebSocket error, will fall back to polling');
      };

    } catch (error) {
      // console.error(/* PROD */ 'Failed to create WebSocket:', error);
      startPolling();
    }
  }, [token, onNotification, startPolling, stopPolling]);

  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
    }
    stopPolling();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [stopPolling]);

  const resetUnreadCount = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const decrementUnreadCount = useCallback(() => {
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  useEffect(() => {
    if (token) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);

  // Ping to keep connection alive (only for WebSocket)
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 25000);

    return () => clearInterval(pingInterval);
  }, []);

  return {
    isConnected,
    unreadCount,
    resetUnreadCount,
    decrementUnreadCount,
    disconnect,
    reconnect: connect,
    isPolling: usePolling.current
  };
}

export default useNotificationWebSocket;
